'use strict';

import { ref } from 'vue';
import { defineStore } from 'pinia';
import { useFetch } from '../../../app/composables/fetch';
import { camelCase, snakeCase } from '../../../app/utils/change-case';

/**
 * Pinia store for a single league
 */
export const useLeagueStore = defineStore('league', () => {
  /**
   * Current page
   */
  const page = ref(1);

  /**
   * Current league
   */
  const league = ref(null);

  /**
   * All Users
   */
  const leagues = ref([]);

  /**
   * Loading
   */
  const loading = ref(false);

  /**
   * Submitting
   */
  const submitting = ref(false);

  /**
   * Error
   */
  const error = ref(null);

  /**
   * Get league by id
   * @param {Number} id - id of league
   * @returns {Void}
   */
  const getById = async (leagueId) => {
    // Clear league
    league.value = null;

    // Reset error
    error.value = null;

    // Call api
    loading.value = true;
    const { data, error: fetchError } = await useFetch(`leagues/${leagueId}`)
      .get()
      .json();
    loading.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      return;
    }

    // Change keys to camel case
    league.value = camelCase(data.value.data);
  };

  /**
   * Get all leagues
   * @returns {Promise} leagues - array of league objects
   */
  const getAll = async () => {
    // Clear leagues
    leagues.value = [];

    // Reset page
    page.value = 1;

    // Reset error
    error.value = null;

    // Call api
    loading.value = true;
    const { data, error: fetchError } = await useFetch('leagues').get().json();
    loading.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      return;
    }

    // Change keys to camel case
    leagues.value = camelCase(data.value.data);
  };

  /**
   * Add league
   * @param {Obejct} league - league object
   * @returns {Promise} league - simple object of league
   */
  const add = async () => {
    // Reset error
    error.value = null;

    // Change keys to snake case
    const payload = snakeCase(league.value);

    // Call api
    submitting.value = true;
    const { data, error: fetchError } = await useFetch('leagues')
      .post(payload)
      .json();
    submitting.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      //throw error.value;
      return;
    }

    // Change keys to camel case
    league.value = camelCase(data.value.data);

    // Add league from response to list
    leagues.value.push(league.value);

    return Promise.resolve(league.value);
  };

  /**
   * Edit league
   * @param {Obejct} league - league object
   * @returns {Promise} league - simple object of league
   */
  const edit = async () => {
    // Reset error
    error.value = null;

    // Change keys to snake case
    const payload = snakeCase(league.value);

    // Call api
    submitting.value = true;
    const { data, error: fetchError } = await useFetch(
      `leagues/${league.value.id}`,
    )
      .patch(payload)
      .json();
    submitting.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      //throw error.value;
      return;
    }

    // Change keys to camel case
    league.value = camelCase(data.value.data);

    return Promise.resolve(league.value);
  };

  /**
   * Delete league
   * @param {Number} id - id of league
   * @returns {Promise} league - simple object of league
   */
  const _delete = async (leagueId) => {
    // Reset error
    error.value = null;

    // Call api
    submitting.value = true;
    const { data, error: fetchError } = await useFetch(`leagues/${leagueId}`)
      .delete()
      .json();
    submitting.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      //throw error.value;
      return;
    }

    // Remove league from list
    leagues.value = leagues.value.filter((league) => league.id !== leagueId);

    return Promise.resolve(true);
  };

  return {
    league,
    leagues,
    error,
    loading,
    submitting,
    getById,
    getAll,
    add,
    edit,
    _delete,
  };
});
